import {
  forwardRef,
  Ref,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { GoogleMapsContext, useMapsLibrary } from '@vis.gl/react-google-maps';

export type MarkerEventProps = {
  onClick?: (e: google.maps.MapMouseEvent | google.maps.IconMouseEvent) => void;
  onDrag?: (e: google.maps.MapMouseEvent) => void;
  onDragStart?: (e: google.maps.MapMouseEvent) => void;
  onDragEnd?: (e: google.maps.MapMouseEvent) => void;
  onMouseOver?: (e: google.maps.MapMouseEvent) => void;
  onMouseOut?: (e: google.maps.MapMouseEvent) => void;
};

export type MarkerPinProps = {
  pinBackground?: string;
  pinBorderColor?: string;
  pinGlyphColor?: string;
  pinGlyph?: string | HTMLElement;
  pinScale?: number;
};

export type MarkerOptions = {
  position: google.maps.LatLng | google.maps.LatLngLiteral;
  title?: string;
  zIndex?: number;
  clickable?: boolean;
  draggable?: boolean;
  visible?: boolean;
};

export type MarkerProps = MarkerOptions & MarkerEventProps & MarkerPinProps;

export type MarkerRef = Ref<google.maps.marker.AdvancedMarkerElement | null>;

function useAdvancedMarker(props: MarkerProps) {
  const {
    onClick,
    onDrag,
    onDragStart,
    onDragEnd,
    onMouseOver,
    onMouseOut,
    pinBackground,
    pinBorderColor,
    pinGlyphColor,
    pinGlyph,
    pinScale,
    ...markerOptions
  } = props;

  // callbacks kept outside deps
  const callbacks = useRef<Record<string, (e: unknown) => void>>({});
  Object.assign(callbacks.current, {
    onClick,
    onDrag,
    onDragStart,
    onDragEnd,
    onMouseOver,
    onMouseOut,
  });

  const markerLibrary = useMapsLibrary('marker');
  const map = useContext(GoogleMapsContext)?.map;

  const advancedMarkerRef =
    useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const pinRef = useRef<google.maps.marker.PinElement | null>(null);

  // Create or update PinElement when style props change
  useMemo(() => {
    if (!markerLibrary) return;

    pinRef.current = new google.maps.marker.PinElement({
      background: pinBackground,
      borderColor: pinBorderColor,
      glyphColor: pinGlyphColor,
      glyph: pinGlyph,
      scale: pinScale,
    });

    // Update advanced marker's content if it exists
    if (advancedMarkerRef.current && pinRef.current) {
      advancedMarkerRef.current.content = pinRef.current.element;
    }
  }, [
    markerLibrary,
    pinBackground,
    pinBorderColor,
    pinGlyphColor,
    pinGlyph,
    pinScale,
  ]);

  // Create marker when map and library are available
  useEffect(() => {
    if (!map) {
      if (map === undefined)
        console.error('<Marker> has to be inside a Map component.');
      return;
    }
    if (!markerLibrary) return;

    if (!advancedMarkerRef.current) {
      advancedMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: markerOptions.position,
        content: pinRef.current ? pinRef.current.element : undefined,
        title: markerOptions.title,
        zIndex: markerOptions.zIndex,
        gmpDraggable: markerOptions.draggable,
        gmpClickable: markerOptions.clickable,
      });
    } else {
      // ensure attached to the correct map
      advancedMarkerRef.current.map =
        markerOptions.visible === false ? null : map;
    }

    return () => {
      if (advancedMarkerRef.current) {
        advancedMarkerRef.current.map = null;
      }
    };
  }, [map, markerLibrary]);

  // Update marker options when they change
  useMemo(() => {
    const marker = advancedMarkerRef.current;
    if (!marker) return;
    marker.position = markerOptions.position as
      | google.maps.LatLng
      | google.maps.LatLngLiteral;
    if (markerOptions.title !== undefined)
      marker.title = markerOptions.title ?? '';
    if (markerOptions.zIndex !== undefined)
      marker.zIndex = markerOptions.zIndex ?? 0;
    if (markerOptions.clickable !== undefined)
      marker.draggable = markerOptions.clickable;
    if (markerOptions.draggable !== undefined)
      marker.draggable = markerOptions.draggable;
    // visibility handled by map assignment
    if (markerOptions.visible !== undefined) {
      marker.map = markerOptions.visible ? (map ?? null) : null;
    }
  }, [
    markerOptions.position,
    markerOptions.title,
    markerOptions.zIndex,
    markerOptions.draggable,
    markerOptions.visible,
    map,
  ]);

  // Attach events
  useEffect(() => {
    const marker = advancedMarkerRef.current;
    if (!marker) return;

    const gme = google.maps.event;

    const listeners = [
      // Standard names (if supported)
      ['click', 'onClick'],
      ['drag', 'onDrag'],
      ['dragstart', 'onDragStart'],
      ['dragend', 'onDragEnd'],
      ['mouseover', 'onMouseOver'],
      ['mouseout', 'onMouseOut'],
      // GMP-prefixed DOM events for AdvancedMarkerElement
      ['gmp-click', 'onClick'],
      ['gmp-drag', 'onDrag'],
      ['gmp-dragstart', 'onDragStart'],
      ['gmp-dragend', 'onDragEnd'],
      ['gmp-mouseover', 'onMouseOver'],
      ['gmp-mouseout', 'onMouseOut'],
    ] as const;

    const handles = listeners.map(([eventName, cbName]) =>
      gme.addListener(marker, eventName, (e: unknown) => {
        const cb = callbacks.current[cbName];
        if (cb) cb(e);
      }),
    );

    return () => {
      handles.forEach((h) => h.remove());
    };
  }, [advancedMarkerRef.current]);

  return advancedMarkerRef.current;
}

export const MapMarker = forwardRef((props: MarkerProps, ref: MarkerRef) => {
  const marker = useAdvancedMarker(props);
  useImperativeHandle(ref, () => marker!, [marker]);
  return null;
});
